import { Navigate, Route, Routes } from 'react-router';
import { AppShell } from '@/components/AppShell';
import {
  RequireArrangementOwner,
  RequireAuth,
  RequireCapability,
  RequireDivision,
  RequireDutyAnalyticsReader,
  RequirePolicyAuthor,
  RequireTemplateAuthor,
} from '@/components/RequireAuth';
import { ANALYTICS_ACCESS, AUDIT_ACCESS, LOCATION_INTEL_ACCESS } from '@/lib/accessRole';
import ArrangementBuilderPage from '@/pages/ArrangementBuilder';
import ArrangementDetailPage from '@/pages/ArrangementDetail';
import ArrangementViewPage from '@/pages/ArrangementView';
import { SURFACES } from '@/lib/surfaces';
import ArrangementsPage from '@/pages/Arrangements';
import TypedArrangementsPage from '@/pages/TypedArrangements';
import DutyFillPage from '@/pages/DutyFill';
import InboxPage from '@/pages/Inbox';
import StandingOrderBuilderPage from '@/pages/StandingOrderBuilder';
import StandingOrdersPage from '@/pages/StandingOrders';
import DashboardPage from '@/pages/Dashboard';
import DashboardTilePage from '@/pages/DashboardTile';
import DutyAnalyticsPage from '@/pages/DutyAnalytics';
import DutyAnalyticsDivisionRoute from '@/pages/DutyAnalytics/DivisionRoute';
import DutyAnalyticsRowsPage from '@/pages/DutyAnalyticsRows';
import LocationIntelPage from '@/pages/LocationIntel';
import DesignPage from '@/pages/Design';
import DownloadAppPage from '@/pages/DownloadApp';
import ForbiddenPage from '@/pages/Forbidden';
import ArrangementPrintPage from '@/pages/ArrangementPrint';
import LoginPage from '@/pages/Login';
import NotFoundPage from '@/pages/NotFound';
import OfficersPage from '@/pages/Officers';
import OrgTreePage from '@/pages/OrgTree';
import PlacesPage from '@/pages/Places';
import PoliciesPage from '@/pages/Policies';
import PolicyBuilderPage from '@/pages/PolicyBuilder';
import PostTypesPage from '@/pages/PostTypes';
import AuditLogPage from '@/pages/AuditLog';
import DashboardLayoutsPage from '@/pages/DashboardLayouts';
import SettingsPage from '@/pages/Settings';
import UsageAnalyticsPage from '@/pages/UsageAnalytics';
import VehicleTypesPage from '@/pages/VehicleTypes';
import TemplatesPage from '@/pages/Templates';
import TemplateLibraryLayout from '@/pages/Templates/Library';
import TemplateLibraryIndex from '@/pages/Templates/Library/LibraryIndex';
import TemplatePartPage from '@/pages/Templates/Library/PartDetail';
import TemplateSectionPage from '@/pages/Templates/Library/SectionDetail';
import TemplateDecisionsPage from '@/pages/Templates/Decisions';
import TemplateCalendarPage from '@/pages/Templates/Calendar';
import TemplateCreatePage from '@/pages/Templates/Create';
import TemplatePublishPage from '@/pages/Templates/Publish';
import TemplateViewPage from '@/pages/Templates/Template';

/**
 * Route table. Pages are `pages/<PascalCase>/index.tsx` per CLAUDE.md §3.
 *
 * `/login` sits OUTSIDE the shell: the shell renders the nav and the account menu,
 * neither of which exists before you have signed in.
 *
 * [amended APP-LINK] `/download-app` is the one other route with no `RequireAuth` — public by
 * design, and its own comment says why. Every other route, inside the shell or out, is guarded.
 *
 * [amended P1a.2] Everything else is a child of a LAYOUT ROUTE rather than being wrapped
 * per route. React already preserved the shell across sibling routes by accident —
 * react-router builds `RenderedRoute` without a key, so reconciliation is by type and
 * position, and `Protected` was one component — but the sidebar now owns real state
 * (collapse, group expansion) and "the shell survives navigation" should be structural
 * rather than emergent. It also deletes six copies of the same two wrappers.
 *
 * The catch-all is last and INSIDE the layout, so an anonymous visitor is bounced to
 * login before learning which routes exist.
 */
const ShellLayout = () => (
  <RequireAuth>
    <AppShell />
  </RequireAuth>
);

export const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />

    {/*
      ⚠️ [APP-LINK] PUBLIC BY DESIGN — THE SECOND ROUTE WITH NO `RequireAuth`, AND THE REASON IS NOT
      `/login`'s. Anybody may ask for the officer app's link, signed in or not, and the page READS
      NOTHING: no session, no officer, no record. Its one request is one public write, which the API
      meters and answers alike for every number. Outside the shell because there is no nav for it to
      sit in, and the print route below is the reminder that "outside" alone never means "unguarded".
    */}
    <Route path="/download-app" element={<DownloadAppPage />} />

    {/*
      ⚠️ [P6c §6] THE PRINTED ORDER SHEET SITS OUTSIDE THE SHELL, AND THAT IS STRUCTURAL.

      `AppShell` is `h-screen overflow-hidden` with one scrolling `<main>` — §8's "THE WINDOW
      NEVER SCROLLS" — and its nav, utility bar and rail have no business on paper. Rendering
      the sheet inside it would mean hiding four regions with `print:hidden` and fighting a
      layout built to be exactly one viewport tall.

      ⚠️ IT IS STILL GUARDED. `/login` is outside the shell because it must be reachable
      without a session; this is outside for a LAYOUT reason, so it keeps both `RequireAuth`
      and the capability the arrangement pages carry. Inheriting `/login`'s absence of a
      guard would have been the fail-open half of the same edit — [P6a] records exactly that
      trap for `/` and the dashboard.
    */}
    <Route
      path="/arrangements/:arrangementId/print"
      element={
        <RequireAuth>
          <RequireCapability capability="arrangement.read">
            <ArrangementPrintPage />
          </RequireCapability>
        </RequireAuth>
      }
    />

    <Route element={<ShellLayout />}>
      {/*
        ⚠️ [P6a] EVERYONE LANDS ON THE DASHBOARD, AND THAT REVERSES [P4].
        `pages/Home` was three static link cards that only a SUPER_ADMIN ever saw: every other
        dashboard role was redirected to `/inbox`, which FR-5.3.1 made the nodal's landing
        screen. A client decision replaces both — the red-count tile drills straight to
        `/inbox`, so a nodal's daily work is one click from a screen that also tells them
        whether there is any.

        ⚠️ `/` REDIRECTS RATHER THAN RENDERING THE DASHBOARD ITSELF, so `/dashboard` is a
        permanent URL: a link anybody has bookmarked or pasted into a WhatsApp group keeps
        working whichever way this is later decided.

        ⚠️ AND THE DASHBOARD CARRIES A `RequireCapability` WHERE `/` NEVER DID. `/` was the one
        route in this table with no gate — inheriting that absence would have been the
        fail-open half of the move.
      */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <RequireCapability capability="dashboard.access">
            <DashboardPage />
          </RequireCapability>
        }
      />
      <Route
        path="/dashboard/tiles/:tileId"
        element={
          <RequireCapability capability="dashboard.access">
            <DashboardTilePage />
          </RequireCapability>
        }
      />
      {/*
        ⚠️ [P7C §2] DUTY ANALYTICS AND ITS DRILL — `dashboard.access`, THEN THE LEVEL.

        No capability is minted: the narrowing is a NODE (the commissionerate or a division), which no
        capability can express, so each route nests `RequireDutyAnalyticsReader` inside the capability
        gate — `/policies/new`'s two-gate shape. The server decides by POSITION and answers a unit-level
        caller 403 with a sentence the page prints; this only stops the page opening for nobody.

        ⚠️ A SIBLING PATH, NOT `/dashboard/analytics`: the Dashboard leaf carries no `end`, so a child
        path would light up two nav rows at once. The drill is `/duty-analytics/rows`, under its own row,
        which carries no `end` either so it stays active there.

        ⚠️ [amended P7C.1 §4] A DIVISION'S OWN PAGE, READ FROM THE COMMISSIONERATE, IS A THIRD ROUTE WITH THE SAME
        TWO GATES AND NO THIRD. Who may read it is a question of POSITION, which the server answers — one 404 for
        every id a division-level caller asks; a web gate could only read the acting unit's TYPE label. Its element
        is `DivisionRoute`, never the page itself: react-router renders a matched route with no key, so the
        commissionerate's page and a division's would otherwise be one instance at one position.
      */}
      <Route
        path="/duty-analytics"
        element={
          <RequireCapability capability="dashboard.access">
            <RequireDutyAnalyticsReader>
              <DutyAnalyticsPage />
            </RequireDutyAnalyticsReader>
          </RequireCapability>
        }
      />
      <Route
        path="/duty-analytics/divisions/:divisionId"
        element={
          <RequireCapability capability="dashboard.access">
            <RequireDutyAnalyticsReader>
              <DutyAnalyticsDivisionRoute />
            </RequireDutyAnalyticsReader>
          </RequireCapability>
        }
      />
      <Route
        path="/duty-analytics/rows"
        element={
          <RequireCapability capability="dashboard.access">
            <RequireDutyAnalyticsReader>
              <DutyAnalyticsRowsPage />
            </RequireDutyAnalyticsReader>
          </RequireCapability>
        }
      />
      {/*
        ⚠️ [LOCATION-INTEL] LOCATIONAL INTEL — `LOCATION_INTEL_ACCESS`, `officer.read` AND THE OFFICER'S OWN FLAG.

        A dashboard officer without the flag — a super admin included — lands on `/forbidden`, as the API refuses
        them; a constable cannot reach a dashboard route at all. This only stops the page opening for somebody
        every request behind it refuses: the three `/location-intel` API routes are the gate, and the caller's
        scope inside them is the control.

        ⚠️ THE PAGE IS STATICALLY IMPORTED, LIKE EVERY OTHER; ITS MAP LIBRARY IS NOT. `mapbox-gl` arrives through a
        dynamic `import()` inside the map component, so this table — which the route tests and the first paint
        import — never evaluates it.
      */}
      <Route
        path="/location-intel"
        element={
          <RequireCapability capability={LOCATION_INTEL_ACCESS}>
            <LocationIntelPage />
          </RequireCapability>
        }
      />
      <Route
        path="/org-tree"
        element={
          <RequireCapability capability="org.read">
            <OrgTreePage />
          </RequireCapability>
        }
      />
      <Route
        path="/officers"
        element={
          <RequireCapability capability="officer.read">
            <OfficersPage />
          </RequireCapability>
        }
      />
      <Route
        path="/places"
        element={
          <RequireCapability capability="place.read">
            <PlacesPage />
          </RequireCapability>
        }
      />
      {/*
        [P3a] The Police Arrangement intake and its list.

        ⚠️ `/arrangements/new` is gated on `arrangement.write`, not on `arrangement.read`,
        following the `/policies/new` precedent: "new" has no read-only meaning and a
        /forbidden bounce is a better answer than a form that cannot be saved.
        `/arrangements/:arrangementId` is gated on READ and renders read-only without the
        write capability, because inspecting an order is exactly what a nodal holds
        `arrangement.read` for — and FR-3.6's scope filter is what decides which orders
        exist for them at all.

        ⚠️ [P3a.1] THE APP'S FIRST list → detail → edit SHAPE, and it earns one where
        `/policies` did not. A published arrangement's dates and places are FROZEN
        (FR-5.2.14), so reading an order is the normal visit and editing it is the
        exception — the reverse of a policy, which exists to be edited. `:arrangementId` is
        therefore the record, `/edit` is the form, and the list sends a DRAFT straight to
        `/edit` because a draft's natural home is still the work.
      */}
      {/*
        [P4] Fulfilment.

        ⚠️ THE FILL SCREEN IS ADDRESSED BY DUTY, NOT NESTED UNDER AN ARRANGEMENT. P3a.1
        called `/duties/:id/detach` "the one exception"; P4 makes it a shape. A nodal arrives
        from an inbox row holding a duty id and not the order's, and FR-3.6 is enforced in the
        QUERY either way — every fill endpoint reaches its arrangement to scope itself.

        ⚠️ GATED ON `.read`, NOT `.write`, and renders read-only without the write capability
        — the `/arrangements/:id` precedent. Seeing who is named is exactly what a NODAL_READ
        holds the capability for.
      */}
      <Route
        path="/inbox"
        element={
          <RequireCapability capability="arrangement.read">
            <InboxPage />
          </RequireCapability>
        }
      />
      <Route
        path="/duties/:dutyId"
        element={
          <RequireCapability capability="arrangement.read">
            <DutyFillPage />
          </RequireCapability>
        }
      />
      <Route
        path="/arrangements"
        element={
          <RequireCapability capability="arrangement.read">
            <ArrangementsPage />
          </RequireCapability>
        }
      />
      {/*
        ⚠️ [P7B.1 §4] A TYPED ARRANGEMENT'S LIST, OUT OF MANAGE ARRANGEMENTS' TAB AND ONTO ITS OWN
        ADDRESS — the same list component and the same guard as `/arrangements`, because it is the same
        read: `arrangement.read`, scoped by the server. An unregistered key renders not-found.
      */}
      <Route
        path="/pa/:typeKey"
        element={
          <RequireCapability capability="arrangement.read">
            <TypedArrangementsPage />
          </RequireCapability>
        }
      />
      <Route
        path="/arrangements/new"
        element={
          <RequireCapability capability="arrangement.write">
            {/* ⚠️ [C1c §7] A UNIT MAY NOT RAISE ONE — and it is not a capability. See the guard. */}
            <RequireDivision>
              <ArrangementBuilderPage />
            </RequireDivision>
          </RequireCapability>
        }
      />
      {/*
        ⚠️ [C3a §4] THE VIEW REPLACED THE RECORD PAGE, AND THE OLD ONE IS HIDDEN RATHER THAN GONE.

        `SURFACES.arrangementRecordPage` is `false`, so this renders `ArrangementViewPage`.
        `pages/ArrangementDetail/**` is not edited by §4 at all — which is what lets its own two
        suites go on passing as the hidden branch's coverage instead of needing a second set of
        assertions written to keep it alive (`surfaces.ts`'s "a test, not a promise" rule).

        ⚠️ BOTH MODULES ARE STATIC IMPORTS AND THAT COSTS NOTHING: `web/` is not route-split, so
        every page in this table is already in the entry graph.
      */}
      <Route
        path="/arrangements/:arrangementId"
        element={
          <RequireCapability capability="arrangement.read">
            {SURFACES.arrangementRecordPage ? <ArrangementDetailPage /> : <ArrangementViewPage />}
          </RequireCapability>
        }
      />
      {/*
        ⚠️ [C3c §9c] THE EDIT ROUTE TAKES `/arrangements/new`'s GUARDS, AND IT CARRIED NEITHER.

        It was `arrangement.read` ALONE. So a `NODAL_READ` officer reached the builder and typed
        into a form whose every save the server refuses, and so did a nodal holding
        `arrangement.write` while `arrangement.nodalAuthor` is off — losing whatever they had
        typed. [C3a] recorded that as a known gap rather than closing it, on the grounds that
        making the builder's own `readOnly` wait on a network-delivered setting reintroduces
        [UX1 §8]'s flip-after-first-paint problem.

        ⚠️ GATING THE ROUTE AVOIDS THAT ENTIRELY, BECAUSE `RequireDivision` ALREADY SOLVES IT.
        It renders `route-gate-waiting` while the setting is in flight and only then decides,
        because `<Navigate replace>` IS NOT UNDOABLE BY A LATER ANSWER. The form is never
        mounted, so there is nothing left that can flip.

        ⚠️ AND `arrangement.read` → `arrangement.write` IS THE LOAD-BEARING HALF. `RequireDivision`
        alone would not catch a read-only officer, who may perfectly well be posted at a division.

        ⚠️ [amended C5 §2b] AND A THIRD GATE, INSIDE THE OTHER TWO: an ISSUED order is edited by
        the officer who raised it, or a super admin. It is INNERMOST because it is the only one
        of the three that reads the RECORD — the two outside it answer from `/auth/me` and a
        setting, so they must refuse before a request for the order is made at all.

        ⚠️ IT COSTS NO EXTRA REQUEST: it reads `queryKeys.arrangements.detail(id)`, the entry
        `ArrangementBuilder` reads for `existing`. And it WAITS, for `RequireDivision`'s reason —
        `<Navigate replace>` cannot be undone by an answer that arrives afterwards.
      */}
      <Route
        path="/arrangements/:arrangementId/edit"
        element={
          <RequireCapability capability="arrangement.write">
            <RequireDivision>
              <RequireArrangementOwner>
                <ArrangementBuilderPage />
              </RequireArrangementOwner>
            </RequireDivision>
          </RequireCapability>
        }
      />

      {/*
        [P2] The configuration screens.

        ⚠️ `/policies/new` is gated on `policy.write`, not on `policy.read` — "new" has no
        read-only meaning, and a /forbidden bounce is a better answer than a form that
        cannot be saved. `/policies/:policyId` is gated on READ and renders read-only
        without the write capability, because inspecting a policy is exactly what a nodal
        holds `policy.read` for.

        ⚠️ [amended C5c §3] AND ON THE SEAT. `policy.write` lists NODAL_ADMIN now, and only one
        at the COMMISSIONERATE may author, so `/policies/new` nests `RequirePolicyAuthor` inside
        the capability — the route's twin of the server's route-plus-service layers. The read
        route is unchanged, and renders read-only unless `mayAuthorPolicy`.
      */}
      {/*
        [P3b.1] Round the clock. `/new` is gated on `.write` rather than `.read` — the rule
        `/arrangements/new` already follows: "new" has no read-only meaning, and a
        `/forbidden` bounce is a better answer than a form that cannot be saved.

        ⚠️ THERE IS NO SEPARATE DETAIL ROUTE. A standing order is a CONFIGURATION that
        exists to be edited — the `/policies/:id` shape, not the `/arrangements/:id` one,
        where a published order is frozen and reading is the normal visit. What it ISSUES
        are arrangements, and those already have a detail page of their own.
      */}
      {/*
        [P7B §4] THE TEMPLATE PAGES — a set's library, its decisions, a year's festival dates, plan &
        create, bulk publish, and one template read-only.

        ⚠️ EVERY ONE IS `arrangement.write` **AND** `RequireTemplateAuthor` — the commissionerate seat the
        API's `assertMayRunTemplates` asks for, nested exactly as `/policies/new` nests its author gate. A
        divisional nodal admin holds the capability and not the seat, and is bounced here rather than
        reaching pages whose every request is refused. No flag: these are not a `master_superadmin` act.

        ⚠️ [amended P7B.1 §4] A SIDEBAR ROW NOW — `Templates`, for a template author only
        (`templateAuthorsOnly`); the typed list has its own row too, so the tab that held both doors is gone.

        ⚠️ [amended P7B.1 §2] THE LIBRARY IS A LAYOUT ROUTE: the tree beside an `<Outlet>`, whose children
        are the index (it opens the first template), one template, one section and one Part. They share
        the layout's guards, so a pane cannot be reached around them.
      */}
      <Route
        path="/templates/:setKey"
        element={
          <RequireCapability capability="arrangement.write">
            <RequireTemplateAuthor>
              <TemplateLibraryLayout />
            </RequireTemplateAuthor>
          </RequireCapability>
        }
      >
        <Route index element={<TemplateLibraryIndex />} />
        <Route path="t/:templateId" element={<TemplateViewPage />} />
        <Route path="section/:sectionKey" element={<TemplateSectionPage />} />
        <Route path="part/:partId" element={<TemplatePartPage />} />
      </Route>
      {(
        [
          ['/templates', <TemplatesPage key="sets" />],
          ['/templates/:setKey/decisions', <TemplateDecisionsPage key="decisions" />],
          ['/templates/:setKey/calendar/:year', <TemplateCalendarPage key="calendar" />],
          ['/templates/:setKey/create', <TemplateCreatePage key="create" />],
          ['/templates/:setKey/publish', <TemplatePublishPage key="publish" />],
        ] as const
      ).map(([path, page]) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireCapability capability="arrangement.write">
              <RequireTemplateAuthor>{page}</RequireTemplateAuthor>
            </RequireCapability>
          }
        />
      ))}

      <Route
        path="/standing-orders"
        element={
          <RequireCapability capability="standingOrder.read">
            <StandingOrdersPage />
          </RequireCapability>
        }
      />
      <Route
        path="/standing-orders/new"
        element={
          <RequireCapability capability="standingOrder.write">
            <StandingOrderBuilderPage />
          </RequireCapability>
        }
      />
      <Route
        path="/standing-orders/:standingOrderId"
        element={
          <RequireCapability capability="standingOrder.read">
            <StandingOrderBuilderPage />
          </RequireCapability>
        }
      />

      <Route
        path="/policies"
        element={
          <RequireCapability capability="policy.read">
            <PoliciesPage />
          </RequireCapability>
        }
      />
      <Route
        path="/policies/new"
        element={
          <RequireCapability capability="policy.write">
            <RequirePolicyAuthor>
              <PolicyBuilderPage />
            </RequirePolicyAuthor>
          </RequireCapability>
        }
      />
      <Route
        path="/policies/:policyId"
        element={
          <RequireCapability capability="policy.read">
            <PolicyBuilderPage />
          </RequireCapability>
        }
      />
      <Route
        path="/post-types"
        element={
          <RequireCapability capability="postType.read">
            <PostTypesPage />
          </RequireCapability>
        }
      />
      <Route
        path="/vehicle-types"
        element={
          <RequireCapability capability="vehicleType.read">
            <VehicleTypesPage />
          </RequireCapability>
        }
      />

      {/*
        [P6b] The dashboard configurator. Gated on the WRITE capability, which has no read
        counterpart — `/policies/new`'s convention: this screen has no read-only meaning, so
        there is no `readOnly` branch in the page and a bounce beats a form that cannot save.

        ⚠️ A SIBLING PATH, NOT `/dashboard/layouts`. The Overview nav leaf carries no `end`
        (deliberately, so `/dashboard/tiles/:tileId` keeps it active), so a child path here
        would light up "Dashboard" in Overview AND "Dashboard tiles" in Configuration at once.
      */}
      <Route
        path="/dashboard-layouts"
        element={
          <RequireCapability capability="dashboard.configure">
            <DashboardLayoutsPage />
          </RequireCapability>
        }
      />

      {/*
        [ANALYTICS §8] THE AUDIT LOG.

        ⚠️ [amended AUDIT-FLAG] `AUDIT_ACCESS`: THE ROLE'S `audit.read` AND THE OFFICER'S OWN
        `access_analytics` FLAG — the same flag `/usage-analytics` below takes, by a client decision
        that reverses [C5b §5]. A super admin who has not been granted it lands on `/forbidden`, as
        the API refuses them; a nodal admin who holds it lands there too, because a flag only adds to
        what the role already allows.

        ⚠️ `/audit-log`, NAMING THE SCREEN. `/audit` is the endpoint it reads, and every row a filter
        narrows to stays on this one path as query parameters, so a filtered view is a shareable link.

        ⚠️ READ-ONLY, SO THE READ KEY, AND THE SERVER IS THE CONTROL: `GET /audit` takes the same
        value, so a nodal who types this path — and a flagless super admin — is bounced here and
        refused there.
      */}
      <Route
        path="/audit-log"
        element={
          <RequireCapability capability={AUDIT_ACCESS}>
            <AuditLogPage />
          </RequireCapability>
        }
      />

      {/*
        [ANALYTICS §5] USAGE ANALYTICS. `analytics.read` — SUPER_ADMIN.

        ⚠️ [amended C5b §5] `ANALYTICS_ACCESS`: THE ROLE'S `analytics.read` AND THE OFFICER'S OWN
        `access_analytics` FLAG. A super admin who has not been granted it lands on `/forbidden`, as
        the API refuses them; a nodal admin who holds it lands there too, because a flag only adds
        to what the role already allows. The value is the one both API routes take.

        ⚠️ A READ PAGE, SO THE READ KEY. It has no write half at all, which is the opposite case
        from `/settings` below: there is nothing on it a `/forbidden` bounce would be protecting
        from a failed save, and the capability IS the question "may they see this".

        ⚠️ THE ROUTE GATE IS A CONVENIENCE AND THE SERVER IS THE CONTROL. `GET /analytics` checks the
        same capability, so a nodal who types the URL is refused twice — here with a bounce before
        the page mounts, and by the API with a 403 whatever this table says.
      */}
      <Route
        path="/usage-analytics"
        element={
          <RequireCapability capability={ANALYTICS_ACCESS}>
            <UsageAnalyticsPage />
          </RequireCapability>
        }
      />

      {/*
        [C1h §1c] SYSTEM-WIDE SETTINGS. `settings.write` — SUPER_ADMIN.

        ⚠️ THE ROUTE GATE IS A CONVENIENCE AND THE SERVER IS THE CONTROL. Both verbs behind
        `/settings/ui-mode` carry the same capability, so a hand-typed URL reaches a page whose
        every request is refused — which is the fail-safe direction and the brief's own rule
        that a screen gated only in the nav is not gated.

        ⚠️ AND IT TAKES THE WRITE KEY THOUGH IT ALSO READS. `/policies/new`'s convention: a
        screen with no read-only meaning gets the write key, because a `/forbidden` bounce is a
        better answer than a settings page that cannot be saved.
      */}
      <Route
        path="/settings"
        element={
          <RequireCapability capability="settings.write">
            <SettingsPage />
          </RequireCapability>
        }
      />

      <Route path="/forbidden" element={<ForbiddenPage />} />

      {/*
        [amended P1a.2] P0's /demo became /design: the same coexistence proof, extended
        into the design gallery that is this pass's review surface and every later pass's
        visual regression surface. Still OFF THE NAV — the nav is domain navigation gated
        by access role, and this is a developer page. Still reachable, because the failure
        it catches is cosmetic rather than a build error.
      */}
      <Route path="/design" element={<DesignPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes>
);
